/**
 * Upload Order — top-level sidebar group for the agent-sheet upload +
 * MIS approval workflow.
 *
 * TAB VISIBILITY (live for all agents and managers):
 *   role 5  (sales agent)   — Sheet Orders tab (upload + view own)
 *   role 14 (floor manager) — Sheet Orders tab (approve/reject/create for team)
 *   MIS / others            — Sheet Orders + MIS Approval tabs
 *
 * DATA SCOPE inside the tab (role-based — no hardcoding):
 *   role 5  (sales agent)   — own orders only
 *   role 14 (floor manager) — tagged agents' orders only
 *   all others              — all orders, full actions
 */

import React from "react";
import { FormattedMessage } from "react-intl";
import { IconFileUpload, IconClipboardCheck } from "@tabler/icons";
import { getDecryptDataFromLocal } from "../../utils/encrypt";

const icons = { IconFileUpload, IconClipboardCheck };

// Live for all active agents (role 5) and managers (role 14) + MIS.
// To restrict again: replace with a whitelist check.
const canSeeUploadOrder = true;

// --- Data scope flags (role-based) ---
const crmRole = Number(getDecryptDataFromLocal('setRole') || 0);
// isMIS: can see all orders + approve/reject/create-order
// isManager: role 14, sees team orders only
// (role 5 agents see only their own orders — enforced in SheetOrderUpload.js + backend)
const isMIS = crmRole !== 5 && crmRole !== 14;

// QR COD Update tab — hardcoded phone allowlist, mirrors the same allowlist enforced
// again on the backend (services/ai-service/routes.js QR_COD_UPDATE_ALLOWLIST).
const QR_COD_UPDATE_ALLOWLIST = ['9315481432', '8826345311', '9719836187', '8969708515'];
const crmPhoneNumber = String(getDecryptDataFromLocal('phoneNumber') || '');
const canSeeQrCodUpdate = QR_COD_UPDATE_ALLOWLIST.includes(crmPhoneNumber);

// Cancellation Requests tab — admins + Ameen only, mirrors role4.js
// _canReviewBulkCancelRequest (role 1/2 or an allowlisted phone). Lalit Sharma
// (9625366141, userType 17) added 2026-09-15 per Shailendra. Keep this array
// in sync with EXTRA_BULK_CANCEL_REVIEW_PHONES in role4.js and
// EXTRA_REVIEW_PHONES in BulkOrderCancellationRequests.js.
const AMEEN_PHONE = '9268096291';
const CANCELLATION_REQUESTS_EXTRA_PHONES = [AMEEN_PHONE, '9625366141'];
const canSeeCancellationRequests = crmRole === 1 || crmRole === 2
  || CANCELLATION_REQUESTS_EXTRA_PHONES.includes(crmPhoneNumber);

// Prime Members tab — admins + Ameen, plus a small named-exception list
// (Ankur, 8969708515, added 2026-08-21 on request) — narrower than QR COD
// Update's phone allowlist above. Backend gate:
// services/ai-service/routes.js _isPrimeAdminOrAmeen — keep both in sync.
// 2026-10-16: managers Abid Ali, Mirza Zahid, OM Singh (role 14), bulk's Ravi
// Shankar Kumar and Lalit Sharma (role 17) added per Shailendra — all members,
// no team scoping. Named people only, not every role 14/17.
const PRIME_MEMBERS_EXTRA_PHONES = [
  '8969708515', // Ankur
  '9625366141', // Lalit Sharma
  '9990619120', // Abid Ali
  '7982526137', // Mirza Zahid
  '8750602205', // OM Singh
  '9911579990', // Ravi Shankar Kumar (bulk)
];
// Manjay Jha (bulk head) by admin _id. What each person sees inside the tab
// is scoped by team on the backend (_primeViewerScope).
const PRIME_MEMBERS_EXTRA_ADMIN_IDS = ['620e24cb40f6563d6cfd27df']; // Manjay Jha
const crmAdminId = String(getDecryptDataFromLocal('_id') || '');
const canSeePrimeMembers = crmRole === 1 || crmRole === 2 || crmPhoneNumber === AMEEN_PHONE
  || PRIME_MEMBERS_EXTRA_PHONES.includes(crmPhoneNumber)
  || PRIME_MEMBERS_EXTRA_ADMIN_IDS.includes(crmAdminId);

const children = [];

if (canSeeUploadOrder) {
  children.push({
    id: "sheet-orders",
    title: <FormattedMessage id="Sheet Orders" />,
    type: "item",
    url: "/admin/sheet-orders",
    icon: icons.IconClipboardCheck,
    breadcrumbs: false,
  });
}

if (canSeeCancellationRequests) {
  children.push({
    id: "bulk-order-cancellation-requests",
    title: <FormattedMessage id="Cancellation Requests" />,
    type: "item",
    url: "/admin/bulk-order-cancellation-requests",
    icon: icons.IconClipboardCheck,
    breadcrumbs: false,
  });
}

if (canSeeQrCodUpdate) {
  children.push({
    id: "qr-cod-update",
    title: <FormattedMessage id="QR COD Update" />,
    type: "item",
    url: "/admin/qr-cod-update",
    icon: icons.IconClipboardCheck,
    breadcrumbs: false,
  });
}

// Prime Members tab — admins + Ameen only (see canSeePrimeMembers above), even
// though it reads/writes the same shared-QR payment collections as QR COD Update.
if (canSeePrimeMembers) {
  children.push({
    id: "prime-members",
    title: <FormattedMessage id="Prime Members" />,
    type: "item",
    url: "/admin/prime-members",
    icon: icons.IconClipboardCheck,
    breadcrumbs: false,
  });
}

if (canSeeUploadOrder && isMIS) {
  // MIS Approval — review queue, approve/reject (MIS team only).
  children.push({
    id: "mis-approval",
    title: <FormattedMessage id="MIS Approval" />,
    type: "item",
    url: "/admin/mis-approval",
    icon: icons.IconClipboardCheck,
    breadcrumbs: false,
  });
}

export const SHOULD_SHOW_UPLOAD_ORDER = canSeeUploadOrder && children.length > 0;

export const UploadOrderTab = {
  id: "upload-order",
  title: <FormattedMessage id="Upload Order" />,
  type: "group",
  children: children.length > 0 ? [
    {
      id: "upload-order-root",
      title: <FormattedMessage id="Upload Order" />,
      type: "collapse",
      icon: icons.IconFileUpload,
      children,
    },
  ] : [],
};
